/**
 * SISP Payments Dashboard
 * Pagina de gestao de pagamentos SISP com estatisticas em tempo real
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid2 as Grid,
  Chip,
  TextField,
  MenuItem,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  LinearProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Payment,
  CheckCircle,
  Cancel,
  Refresh,
  FileDownload,
  Undo,
  CreditCard,
  Receipt,
  Timeline,
  Error as ErrorIcon,
  AccessTime,
} from '@mui/icons-material';
import DataTable, { Column, StatusChip, CurrencyCell, DateCell } from '../../../components/Common/DataTable';
import { sispService, SISPPayment, SISPPaymentDetail, SISPStats } from '../../../services/sisp.service';

/**
 * Valores de `merchantResp` que significam aprovado.
 *
 * 'C' vem do fluxo antigo (72 909 respostas em produção) e '0' da tokenização
 * (51). Só com 'C', os pagamentos com token apareciam a vermelho como «Erro»
 * apesar de estarem aprovados e terem código de autorização.
 */
const APROVADO = ['C', '0'];

// ============================================
// Stats Card Component
// ============================================

interface StatsCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color: 'primary' | 'success' | 'warning' | 'error' | 'info';
  loading?: boolean;
}

const StatsCard: React.FC<StatsCardProps> = ({ title, value, subtitle, icon, color, loading }) => (
  <Card>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: `${color}.light`,
            color: `${color}.main`,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <Typography variant="h5" fontWeight={700}>
                {value}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {title}
              </Typography>
              {subtitle && (
                <Typography variant="caption" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ============================================
// Payment Details Dialog
// ============================================

interface PaymentDialogProps {
  open: boolean;
  onClose: () => void;
  paymentId: string | null;
}

const PaymentDetailsDialog: React.FC<PaymentDialogProps> = ({ open, onClose, paymentId }) => {
  const [payment, setPayment] = useState<SISPPaymentDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (open && paymentId) {
      fetchPaymentDetails();
    }
  }, [open, paymentId]);

  const fetchPaymentDetails = async () => {
    if (!paymentId) return;
    setLoading(true);
    try {
      const response = await sispService.getPaymentDetails(paymentId);
      setPayment(response.data);
    } catch (error) {
      console.error('Error fetching payment details:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Receipt />
          Detalhes do Pagamento
          {payment && (
            <Chip
              label={sispService.getStatusLabel(payment.status)}
              color={sispService.getStatusColor(payment.status)}
              size="small"
            />
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : payment ? (
          <Box>
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
              <Tab label="Informacoes" />
              <Tab label="Respostas SISP" />
              <Tab label="Estornos" />
            </Tabs>

            {activeTab === 0 && (
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Referencia</Typography>
                  <Typography variant="body1" fontWeight={500}>{payment.merchantRef}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Sessao</Typography>
                  <Typography variant="body1">{payment.merchantSession}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Montante</Typography>
                  <Typography variant="body1" fontWeight={700} color="primary">
                    {sispService.formatCurrency(payment.amount)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Metodo</Typography>
                  <Typography variant="body1">{payment.paymentMethod?.toUpperCase()}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Origem</Typography>
                  <Typography variant="body1">{sispService.getOriginLabel(payment.origin)}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Data</Typography>
                  <Typography variant="body1">
                    {new Date(payment.createdAt).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })}
                  </Typography>
                </Grid>
                {payment.customerName && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary">Cliente</Typography>
                    <Typography variant="body1">{payment.customerName}</Typography>
                  </Grid>
                )}
                {payment.entityName && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary">Entidade</Typography>
                    <Typography variant="body1">{payment.entityName}</Typography>
                  </Grid>
                )}
                {payment.transactionId && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary">Transaction ID</Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {payment.transactionId}
                    </Typography>
                  </Grid>
                )}
                {payment.clearingPeriod && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" color="text.secondary">Clearing Period</Typography>
                    <Typography variant="body1">{payment.clearingPeriod}</Typography>
                  </Grid>
                )}
                {payment.errorCode && (
                  <Grid size={{ xs: 12 }}>
                    <Alert severity="error" icon={<ErrorIcon />}>
                      <Typography variant="subtitle2">Erro: {payment.errorCode}</Typography>
                      <Typography variant="body2">{payment.errorDescription}</Typography>
                    </Alert>
                  </Grid>
                )}
              </Grid>
            )}

            {activeTab === 1 && (
              <Box>
                {payment.responses && payment.responses.length > 0 ? (
                  payment.responses.map((resp, idx) => (
                    <Paper key={idx} sx={{ p: 2, mb: 2 }}>
                      <Grid container spacing={1}>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary">Message Type</Typography>
                          <Typography variant="body2">{resp.messageType}</Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary">Response</Typography>
                          <Chip
                            label={APROVADO.includes(resp.merchantResp) ? 'Confirmado' : 'Erro'}
                            color={APROVADO.includes(resp.merchantResp) ? 'success' : 'error'}
                            size="small"
                          />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary">TID</Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {resp.merchantRespTid || '-'}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary">CP</Typography>
                          <Typography variant="body2">{resp.merchantRespCP || '-'}</Typography>
                        </Grid>
                        {resp.merchantRespErrorCode && (
                          <Grid size={{ xs: 12 }}>
                            <Alert severity="warning" sx={{ mt: 1 }}>
                              {resp.merchantRespErrorCode}: {resp.merchantRespErrorDescription}
                            </Alert>
                          </Grid>
                        )}
                      </Grid>
                    </Paper>
                  ))
                ) : (
                  <Typography color="text.secondary">Sem respostas registadas</Typography>
                )}
              </Box>
            )}

            {activeTab === 2 && (
              <Box>
                {payment.refunds && payment.refunds.length > 0 ? (
                  payment.refunds.map((refund, idx) => (
                    <Paper key={idx} sx={{ p: 2, mb: 2 }}>
                      <Grid container spacing={1}>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary">Referencia</Typography>
                          <Typography variant="body2">{refund.merchantRef}</Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary">Montante</Typography>
                          <Typography variant="body2">{sispService.formatCurrency(refund.amount)}</Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary">Estado</Typography>
                          <Chip
                            label={sispService.getStatusLabel(refund.status)}
                            color={sispService.getStatusColor(refund.status)}
                            size="small"
                          />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary">Data</Typography>
                          <Typography variant="body2">
                            {new Date(refund.createdAt).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))
                ) : (
                  <Typography color="text.secondary">Sem estornos registados</Typography>
                )}
              </Box>
            )}
          </Box>
        ) : (
          <Typography color="text.secondary">Pagamento nao encontrado</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================
// Refund Dialog
// ============================================

interface RefundDialogProps {
  open: boolean;
  onClose: () => void;
  payment: SISPPayment | null;
  onSuccess: (mensagem: string, houveAviso: boolean) => void;
}

const RefundDialog: React.FC<RefundDialogProps> = ({ open, onClose, payment, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [tipo, setTipo] = useState<'total' | 'parcial'>('total');
  const [valorParcial, setValorParcial] = useState('');

  // Reset ao abrir noutro pagamento — senão o valor do anterior fica lá, e num
  // ecrã que move dinheiro isso é o pior tipo de estado residual.
  useEffect(() => {
    if (open) { setTipo('total'); setValorParcial(''); setMotivo(''); setError(null); }
  }, [open, payment?.id]);

  const totalPago = Math.round(payment?.amount || 0);
  const valorNum = Number(valorParcial);
  const parcialInvalido = tipo === 'parcial' && (
    !valorParcial || !Number.isFinite(valorNum) || valorNum <= 0 || valorNum > totalPago
  );

  const handleRefund = async () => {
    if (!payment) return;
    setLoading(true);
    setError(null);
    try {
      const response = await sispService.requestRefund(
        payment.id, motivo, tipo === 'parcial' ? valorNum : undefined,
      );
      if (response.success) {
        // O backend devolve `data.ledger` com o que foi revertido na
        // contabilidade, e um aviso quando NÃO conseguiu reverter — nesse caso
        // a dívida à entidade fica de pé e alguém tem de a corrigir à mão. Não
        // dá para fechar o ecrã sem isso ser dito.
        const ledger = (response as any)?.data?.ledger;
        const aviso: string | undefined = ledger?.aviso;
        const valor = Math.round(payment.amount || 0).toLocaleString('pt-PT');

        const d: any = (response as any)?.data ?? {};
        const montante = tipo === 'parcial'
          ? valorNum.toLocaleString('pt-PT')
          : valor;

        const partes = [
          tipo === 'parcial'
            ? `Devolução parcial de ${montante}$ confirmada. O valor volta ao cartão usado na compra.`
            : `Estorno de ${montante}$ efectuado. O valor volta ao cartão usado na compra.`,
        ];
        // Totais vindos do histórico da SISP — só existem na devolução parcial.
        if (d.totalDevolvido != null) {
          partes.push(
            `Já devolvido ${Number(d.totalDevolvido).toLocaleString('pt-PT')}$; ` +
            `por devolver ${Number(d.porDevolver ?? 0).toLocaleString('pt-PT')}$.`
          );
        }
        if (ledger?.revertido) {
          partes.push(`Contabilidade revertida (${ledger.bilhetes} bilhete(s)).`);
        }
        if (aviso) partes.push(`⚠️ ${aviso}`);

        onSuccess(partes.join(' '), Boolean(aviso));
        onClose();
      } else {
        setError(response.message || 'Erro ao processar estorno');
      }
    } catch (err: any) {
      // O `err.message` do axios é sempre «Request failed with status code 400»
      // e esconde o motivo real, que vem no corpo da resposta. Era por isso que
      // o ecrã dizia apenas que não funcionava, sem dizer porquê.
      const corpo = err?.response?.data;
      setError(
        corpo?.error
          ? `${corpo.error}${corpo.codigo ? ` (${corpo.codigo})` : ''}`
          : corpo?.message || err?.message || 'Erro ao processar estorno'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Undo />
          Solicitar Estorno
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {payment && (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Tem a certeza que deseja estornar este pagamento?
            </Alert>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Referencia</Typography>
                <Typography variant="body1">{payment.merchantRef}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Montante</Typography>
                <Typography variant="body1" fontWeight={700} color="error">
                  {sispService.formatCurrency(payment.amount)}
                </Typography>
              </Grid>
            </Grid>

            {/* Total ou parcial. O total é o primeiro e o pré-seleccionado:
                é a operação de sempre, e quem só quer estornar tudo não deve
                ter de decidir nada. */}
            <TextField
              select
              fullWidth
              label="Tipo de devolução"
              value={tipo}
              onChange={(e) => setTipo(e.target.value as 'total' | 'parcial')}
              sx={{ mt: 2 }}
            >
              <MenuItem value="total">Estorno total — devolve tudo</MenuItem>
              <MenuItem value="parcial">Devolução parcial — devolve só uma parte</MenuItem>
            </TextField>

            {tipo === 'parcial' && (
              <TextField
                fullWidth
                type="number"
                label="Valor a devolver (CVE)"
                value={valorParcial}
                onChange={(e) => setValorParcial(e.target.value)}
                error={Boolean(valorParcial) && parcialInvalido}
                helperText={
                  Boolean(valorParcial) && parcialInvalido
                    ? `Tem de ser entre 1 e ${totalPago.toLocaleString('pt-PT')}$`
                    : `Máximo ${totalPago.toLocaleString('pt-PT')}$. ` +
                      `Se este pagamento já teve devoluções, o disponível é menor — a SISP recusa o excesso.`
                }
                inputProps={{ min: 1, max: totalPago, step: 1 }}
                sx={{ mt: 2 }}
              />
            )}

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Motivo do Estorno"
              placeholder="Descreva o motivo do estorno..."
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              sx={{ mt: 2 }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleRefund}
          disabled={loading || parcialInvalido}
          startIcon={loading ? <CircularProgress size={20} /> : <Undo />}
        >
          {tipo === 'parcial' ? 'Confirmar Devolução Parcial' : 'Confirmar Estorno'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================
// Main SISP Payments Page
// ============================================

const SISPPayments: React.FC = () => {
  // State
  const [payments, setPayments] = useState<SISPPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refundFeedback, setRefundFeedback] = useState<
    { mensagem: string; severidade: 'success' | 'warning' } | null
  >(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [originFilter, setOriginFilter] = useState('all');
  const [stats, setStats] = useState<SISPStats | null>(null);

  // Dialog states
  const [detailsDialog, setDetailsDialog] = useState<{ open: boolean; paymentId: string | null }>({
    open: false,
    paymentId: null,
  });
  const [refundDialog, setRefundDialog] = useState<{ open: boolean; payment: SISPPayment | null }>({
    open: false,
    payment: null,
  });

  // Fetch payments
  const fetchPayments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {
        page: page + 1,
        limit: rowsPerPage,
        ...(searchQuery && { search: searchQuery }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(originFilter !== 'all' && { origem: originFilter }),
      };

      const response = await sispService.getPayments(filters);
      setPayments(response.data);
      setTotalCount(response.pagination?.total || 0);
    } catch (err: any) {
      console.error('Error fetching payments:', err);
      setError(err.message || 'Erro ao carregar pagamentos');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, statusFilter, originFilter]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await sispService.getStats(30);
      setStats(response.data.summary);
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Effects
  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Table columns
  const columns: Column<SISPPayment>[] = [
    {
      id: 'merchantRef',
      label: 'Referencia',
      minWidth: 130,
      format: (value) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
          {value}
        </Typography>
      ),
    },
    {
      id: 'customerName',
      label: 'Cliente',
      minWidth: 150,
      format: (value) => value || <span style={{ color: '#999' }}>-</span>,
    },
    {
      id: 'entityName',
      label: 'Entidade',
      minWidth: 150,
      format: (value) => value || <span style={{ color: '#999' }}>-</span>,
    },
    {
      id: 'amount',
      label: 'Montante',
      minWidth: 100,
      align: 'right',
      format: (value) => <CurrencyCell value={value} />,
    },
    {
      id: 'origin',
      label: 'Origem',
      minWidth: 100,
      format: (value) => (
        <Chip label={sispService.getOriginLabel(value)} size="small" variant="outlined" />
      ),
    },
    {
      id: 'paymentMethod',
      label: 'Metodo',
      minWidth: 80,
      format: (value) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <CreditCard fontSize="small" />
          {value?.toUpperCase()}
        </Box>
      ),
    },
    {
      id: 'createdAt',
      label: 'Data',
      minWidth: 140,
      format: (value) => <DateCell value={value} includeTime />,
    },
    {
      id: 'status',
      label: 'Estado',
      minWidth: 100,
      align: 'center',
      format: (value) => (
        <StatusChip
          status={sispService.getStatusLabel(value)}
          colorMap={{
            Submetido: 'warning',
            Pago: 'success',
            Falhou: 'error',
            Estornado: 'info',
          }}
        />
      ),
    },
  ];

  // Handlers
  const handleView = (payment: SISPPayment) => {
    setDetailsDialog({ open: true, paymentId: payment.id });
  };

  const handleRefund = (payment: SISPPayment) => {
    setRefundDialog({ open: true, payment });
  };

  // O estorno fechava o diálogo em silêncio: o operador não sabia se tinha
  // corrido bem, e o aviso do ledger — «a dívida à entidade continua
  // registada» — nunca chegava a aparecer em lado nenhum.
  const handleRefundSuccess = (mensagem: string, houveAviso: boolean) => {
    setRefundFeedback({ mensagem, severidade: houveAviso ? 'warning' : 'success' });
    fetchPayments();
    fetchStats();
  };

  const formatCurrency = (value: number) =>
    `${Math.round(value || 0).toLocaleString('pt-PT')}$`;

  // Render
  return (
    <Box>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Resultado do estorno — fica até ser fechado, para o operador poder ler
          o aviso do ledger com calma em vez de o ver passar num toast. */}
      {refundFeedback && (
        <Alert
          severity={refundFeedback.severidade}
          sx={{ mb: 3 }}
          onClose={() => setRefundFeedback(null)}
        >
          {refundFeedback.mensagem}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Pagamentos SISP
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gateway Vinti4 - Gestao de pagamentos em tempo real
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<FileDownload />}>
            Exportar
          </Button>
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={() => { fetchPayments(); fetchStats(); }}
          >
            Atualizar
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title="Total Transacoes"
            value={stats?.total || 0}
            icon={<Payment />}
            color="primary"
            loading={statsLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title="Pagos"
            value={stats?.success || 0}
            subtitle={`${formatCurrency(stats?.successAmount || 0)}`}
            icon={<CheckCircle />}
            color="success"
            loading={statsLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title="Pendentes"
            value={stats?.pending || 0}
            icon={<AccessTime />}
            color="warning"
            loading={statsLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatsCard
            title="Falhados"
            value={stats?.failed || 0}
            icon={<Cancel />}
            color="error"
            loading={statsLoading}
          />
        </Grid>
      </Grid>

      {/* Success Rate */}
      {stats && stats.total > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Timeline />
            <Typography variant="subtitle1" fontWeight={600}>
              Taxa de Sucesso
            </Typography>
            <Typography variant="h6" color="success.main" fontWeight={700}>
              {((stats.success / stats.total) * 100).toFixed(1)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={(stats.success / stats.total) * 100}
            color="success"
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Paper>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Pesquisar"
              placeholder="Referencia, cliente..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              select
              label="Estado"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="S">Submetidos</MenuItem>
              <MenuItem value="N">Pagos</MenuItem>
              <MenuItem value="F">Falhados</MenuItem>
              <MenuItem value="E">Estornados</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              select
              label="Origem"
              value={originFilter}
              onChange={(e) => setOriginFilter(e.target.value)}
            >
              <MenuItem value="all">Todas</MenuItem>
              <MenuItem value="WEBS">Consulta AEPS</MenuItem>
              <MenuItem value="WEBSR">Servico Referencia</MenuItem>
              <MenuItem value="RCG">Recarga</MenuItem>
              <MenuItem value="EVT">Evento</MenuItem>
              <MenuItem value="DNT">Donativo</MenuItem>
              <MenuItem value="ECM">E-commerce</MenuItem>
              <MenuItem value="FAT">Fatura</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setSearchQuery('');
                setStatusFilter('all');
                setOriginFilter('all');
              }}
            >
              Limpar Filtros
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={payments}
        loading={loading}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(rpp) => {
          setRowsPerPage(rpp);
          setPage(0);
        }}
        onRefresh={fetchPayments}
        onView={handleView}
        extraActions={(row) =>
          row.status === 'N' ? (
            <Tooltip title="Estornar pagamento">
              <IconButton size="small" color="warning" onClick={() => handleRefund(row)}>
                <Undo fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null
        }
        rowKey="id"
        emptyMessage="Nenhum pagamento SISP encontrado"
      />

      {/* Dialogs */}
      <PaymentDetailsDialog
        open={detailsDialog.open}
        onClose={() => setDetailsDialog({ open: false, paymentId: null })}
        paymentId={detailsDialog.paymentId}
      />

      <RefundDialog
        open={refundDialog.open}
        onClose={() => setRefundDialog({ open: false, payment: null })}
        payment={refundDialog.payment}
        onSuccess={handleRefundSuccess}
      />
    </Box>
  );
};

export default SISPPayments;
